import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AuthService } from '../../services/authService';
import { userFacingError } from '../../utils/userFacingError';
import { useConfirmation } from '../../contexts/ConfirmationContext';
import { Button } from './Button';
import { Input } from './Input';
import Modal from './Modal';

export const ChangePasswordModal: React.FC<{
  isOpen: boolean; onClose: () => void; onSuccess?: () => void;
  onDirtyChange?: (dirty: boolean, busy: boolean) => void;
}> = ({ isOpen, onClose, onSuccess, onDirtyChange }) => {
  const confirm = useConfirmation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatedPassword, setRepeatedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const dirty = !!(currentPassword || newPassword || repeatedPassword);
  useEffect(() => { onDirtyChange?.(isOpen && dirty, isLoading); }, [dirty, isOpen, isLoading, onDirtyChange]);
  const reset = () => { setCurrentPassword(''); setNewPassword(''); setRepeatedPassword(''); setError(''); setShowPassword(false); };
  const close = async () => {
    if (isLoading) return;
    if (dirty && !(await confirm({ title: 'Passworteingaben verwerfen?', description: 'Die eingegebenen Passwörter werden entfernt. Dein bisheriges Passwort bleibt gültig.', confirmLabel: 'Eingaben verwerfen', danger: true }))) return;
    reset(); onClose();
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setError('');
    if (newPassword.length < 8) { setError('Das neue Passwort muss mindestens acht Zeichen enthalten.'); return; }
    if (newPassword === currentPassword) { setError('Bitte wähle ein anderes Passwort als bisher.'); return; }
    if (newPassword !== repeatedPassword) { setError('Die neuen Passwörter stimmen nicht überein.'); return; }
    setIsLoading(true);
    try {
      await AuthService.changePassword(currentPassword, newPassword);
      reset(); toast.success('Passwort geändert.'); onSuccess?.(); onClose();
    } catch (e) { setError(userFacingError(e, 'Das Passwort konnte nicht geändert werden. Bitte versuche es erneut.')); }
    finally { setIsLoading(false); }
  };
  if (!isOpen) return null;
  return <Modal title="Passwort ändern" onClose={close} busy={isLoading}>
    <form onSubmit={submit} className="space-y-4">
      <Input label="Aktuelles Passwort" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required disabled={isLoading} />
      <Input label="Neues Passwort" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} helperText="Mindestens acht Zeichen" required disabled={isLoading} />
      <Input label="Neues Passwort bestätigen" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={repeatedPassword} onChange={e => setRepeatedPassword(e.target.value)} required disabled={isLoading} />
      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={showPassword} onChange={e => setShowPassword(e.target.checked)} />Passwörter anzeigen</label>
      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      <div className="action-row"><Button variant="outline" onClick={close} disabled={isLoading}>Abbrechen</Button><Button type="submit" loading={isLoading}>Passwort ändern</Button></div>
    </form>
  </Modal>;
};
